import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon, Lock as LockIcon, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, EditNote as EditNoteIcon, InfoOutlined as InfoOutlinedIcon, Add as AddIcon, Visibility as VisibilityIcon, History as HistoryIcon } from '@mui/icons-material'
import { useAuthStore } from '@/stores/authStore'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import '@/styles/dashboard.css'
import { ROUTES } from '@/utils/constants'
import type {
  EditorJsData,
  Homework,
  Lesson,
  LessonNote,
  LessonNoteEditingPayload,
  LessonNoteEditingState,
  LessonNoteVisibility,
  LessonParticipant,
  LessonPreviousNote,
  LessonStatus,
  LessonWordGroup,
  Test,
} from '@/types'
import { lessonsApi } from '@/api/lessons'
import { lessonNotesApi } from '@/api/lessonNotes'
import { lessonWordsApi } from '@/api/lessonWords'
import { homeworksApi } from '@/api/homeworks'
import { testsApi } from '@/api/tests'
import { getErrorMessage } from '@/utils/error'
import { canRescheduleLesson } from '@/utils/lesson'
import { PlayArrow as PlayArrowIcon, Stop as StopIcon, Cancel as CancelIcon, Event as EventIcon } from '@mui/icons-material'
import { EditorJsField } from '@/components/editor/EditorJsField'
import { LessonRescheduledChip } from '@/components/lessons/LessonRescheduledChip'
import { LessonRescheduleDialog } from '@/components/lessons/LessonRescheduleDialog'
import { LessonWordsPanel } from '@/components/lessons/LessonWordsPanel'
import { LessonPostFinishWizard } from '@/components/lessons/LessonPostFinishWizard'
import { useLessonRealtime, type LessonRealtimeUpdateType } from '@/realtime/useLessonRealtime'
import { LessonPaymentBanner } from '@/components/billing/LessonPaymentBanner'
import { LessonPricePreview } from '@/components/billing/LessonPricePreview'
import { LessonStartSoonBanner } from '@/components/notifications/LessonStartSoonBanner'
import { PaymentStatusChip } from '@/components/billing/PaymentStatusChip'
import { formatMoney, formatPriceSource } from '@/components/billing/formatMoney'
import { formatDateTimeDMY, formatLessonTimeRange } from '@/utils/date'
import {
  lessonNoteCaptionMutedSx,
  lessonNoteCardMutedSx,
  lessonNoteCollapseBtnMutedSx,
  lessonNoteEditorHolderMutedSx,
  lessonNoteIconMutedSx,
  lessonNoteTitleMutedSx,
} from '@/styles/lessonNotesMuted'

const emptyEditorJs: EditorJsData = { blocks: [], time: undefined, version: '2.0.0' }

/** Пустое тело Editor.js (нет блоков или один пустой параграф) — не подменяем им чужой контент по сокету. */
function editorJsDataIsEffectivelyEmpty(data: EditorJsData | undefined | null): boolean {
  if (data == null) return true
  const blocks = data.blocks
  if (!Array.isArray(blocks) || blocks.length === 0) return true
  if (blocks.length === 1 && blocks[0]?.type === 'paragraph') {
    const text = String((blocks[0] as { data?: { text?: string } }).data?.text ?? '').trim()
    return text.length === 0
  }
  return false
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.trim().toLowerCase()
  const match = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalized)
  if (!match) return null
  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16),
  }
}

function withAlpha(color: string, alpha: number): string {
  const rgb = hexToRgb(color)
  if (!rgb) return color
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

function toTs(value?: string | null): number {
  if (!value) return 0
  const ts = Date.parse(value)
  return Number.isFinite(ts) ? ts : 0
}

const PREVIOUS_NOTE_PREVIEW_MAX = 200

function truncateNotePreview(text: string, maxLength = PREVIOUS_NOTE_PREVIEW_MAX): string {
  const normalized = text.trim()
  if (normalized.length <= maxLength) return normalized
  return `${normalized.slice(0, maxLength)}…`
}

type PreviousNoteSectionProps = {
  previousNote: LessonPreviousNote
  visibility: LessonNoteVisibility
  muted?: boolean
  t: (key: string, defaultValue: string, options?: Record<string, unknown>) => string
}

function PreviousNoteSection({ previousNote, visibility, muted = false, t }: PreviousNoteSectionProps) {
  const note = previousNote.notes[visibility]

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: muted ? 'action.hover' : 'background.default',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon fontSize="small" sx={{ color: muted ? 'text.disabled' : 'primary.main', flexShrink: 0 }} />
        <Typography variant="subtitle2" fontWeight={700} sx={muted ? { color: 'text.disabled' } : undefined}>
          {t('lessonNotes.previousNote.title', 'Заметка с предыдущего урока')}
        </Typography>
      </Box>

      <Typography variant="body2" sx={muted ? lessonNoteCaptionMutedSx : { color: 'text.secondary' }}>
        {t('lessonNotes.previousNote.lessonDate', 'Урок от {{date}}', {
          date: formatDateTimeDMY(previousNote.lesson.planned_start_at),
        })}
        {' · '}
        <Link
          component={RouterLink}
          to={`${ROUTES.LESSONS}/${previousNote.lesson.id}`}
          underline="hover"
          color="inherit"
          sx={{ fontWeight: 600 }}
        >
          {t('lessonNotes.previousNote.openLesson', 'Открыть прошлый урок')}
        </Link>
      </Typography>

      {note?.body_text ? (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: muted ? 'text.disabled' : 'text.primary',
          }}
        >
          {truncateNotePreview(note.body_text)}
        </Typography>
      ) : (
        <Typography variant="body2" sx={muted ? lessonNoteCaptionMutedSx : { color: 'text.secondary' }}>
          {t('lessonNotes.previousNote.noPreview', 'Нет доступной заметки с прошлого урока')}
        </Typography>
      )}
    </Box>
  )
}

export function LessonDetailsPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const lessonId = id ? parseInt(id, 10) : NaN

  const authUserId = useAuthStore((s) => s.user?.id) ?? null
  /** К списку уроков: единый URL `/lessons` для всех ролей. */
  const lessonsListPath = useMemo(() => {
    return ROUTES.LESSONS
  }, [])
  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [participants, setParticipants] = useState<LessonParticipant[]>([])
  const [notes, setNotes] = useState<LessonNote[]>([])
  const [previousNote, setPreviousNote] = useState<LessonPreviousNote | null>(null)

  const [sharedDraft, setSharedDraft] = useState<EditorJsData>(emptyEditorJs)
  const [privateTeacherDraft, setPrivateTeacherDraft] = useState<EditorJsData>(emptyEditorJs)
  const [isSharedDirty, setIsSharedDirty] = useState(false)
  const [isPrivateTeacherDirty, setIsPrivateTeacherDirty] = useState(false)
  const [isSharedNotesOpen, setIsSharedNotesOpen] = useState(false)
  const [isPrivateNotesOpen, setIsPrivateNotesOpen] = useState(false)
  const [isNoteRealtimeConflict, setIsNoteRealtimeConflict] = useState(false)
  const [sharedNoteSaveError, setSharedNoteSaveError] = useState<string | null>(null)
  const [privateTeacherNoteSaveError, setPrivateTeacherNoteSaveError] = useState<string | null>(null)

  const [postFinishWizardOpen, setPostFinishWizardOpen] = useState(false)
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false)
  const [homeworkDraft, setHomeworkDraft] = useState<EditorJsData>(emptyEditorJs)
  const [homeworkSaveError, setHomeworkSaveError] = useState<string | null>(null)
  const [submittingHomework, setSubmittingHomework] = useState(false)
  const [homeworkEditorNonce, setHomeworkEditorNonce] = useState(0)

  const [homeworkViewModalOpen, setHomeworkViewModalOpen] = useState(false)
  const [homeworkToView, setHomeworkToView] = useState<Homework | null>(null)

  const [lessonHomeworks, setLessonHomeworks] = useState<Homework[]>([])
  const [lessonHomeworksLoading, setLessonHomeworksLoading] = useState(false)
  const [lessonHomeworksError, setLessonHomeworksError] = useState<string | null>(null)

  const [lessonTests, setLessonTests] = useState<Test[]>([])
  const [lessonTestsLoading, setLessonTestsLoading] = useState(false)
  const [lessonTestsError, setLessonTestsError] = useState<string | null>(null)

  const [lessonWords, setLessonWords] = useState<LessonWordGroup[]>([])
  const [lessonWordsLoading, setLessonWordsLoading] = useState(false)
  const [lessonWordsError, setLessonWordsError] = useState<string | null>(null)

  const [nowTick, setNowTick] = useState(() => Date.now())

  const isLessonTeacher = useMemo(() => {
    if (!lesson || !authUserId) return false
    return lesson.teacher_id === authUserId
  }, [lesson, authUserId])

  const isLessonParticipant = useMemo(() => {
    if (!authUserId) return false
    return participants.some((p) => p.user_id === authUserId)
  }, [participants, authUserId])

  const canEditSharedNotes = useMemo(() => {
    if (!lesson) return false
    if (lesson.status !== 'in_progress') return false
    return Boolean(isLessonTeacher || isLessonParticipant)
  }, [lesson, isLessonTeacher, isLessonParticipant])

  const canEditPrivateTeacherNotes = useMemo(() => {
    if (!lesson) return false
    if (lesson.status !== 'in_progress') return false
    return Boolean(isLessonTeacher)
  }, [lesson, isLessonTeacher])

  /** Внешний вид блоков заметок: «как disabled», пока урок не идёт (planned) или уже завершён/отменён */
  const notesMutedByLessonStatus = useMemo(
    () => Boolean(lesson && lesson.status !== 'in_progress'),
    [lesson]
  )

  const [noteEditingRemote, setNoteEditingRemote] = useState<LessonNoteEditingState>({
    shared: null,
    private_teacher: null,
  })
  const holdingSharedRef = useRef(false)
  const holdingPrivateRef = useRef(false)
  const sharedLiveSyncTimerRef = useRef<number | null>(null)
  const privateLiveSyncTimerRef = useRef<number | null>(null)
  const [editingSharedFocus, setEditingSharedFocus] = useState(false)
  const [editingPrivateFocus, setEditingPrivateFocus] = useState(false)
  const [noteEditingExpiredMessage, setNoteEditingExpiredMessage] = useState<string | null>(null)

  const handleNoteEditingHeartbeat423 = useCallback(
    (visibility: LessonNoteVisibility) => {
      if (visibility === 'shared') {
        holdingSharedRef.current = false
        setEditingSharedFocus(false)
      } else {
        holdingPrivateRef.current = false
        setEditingPrivateFocus(false)
      }
      setNoteEditingExpiredMessage(
        t('lessonNotes.editingSessionExpired', 'Сессия редактирования истекла или занята другим пользователем.')
      )
      if (Number.isFinite(lessonId)) {
        void lessonNotesApi.getNoteEditingState(lessonId).then(setNoteEditingRemote).catch(() => {})
      }
    },
    [lessonId, t]
  )

  const pulseNoteEditingHeartbeat = useCallback(
    async (visibility: LessonNoteVisibility, bodyJson?: EditorJsData) => {
      if (!Number.isFinite(lessonId)) return
      try {
        await lessonNotesApi.heartbeatNoteEditing(lessonId, visibility, bodyJson)
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.status === 423) {
          handleNoteEditingHeartbeat423(visibility)
          return
        }
        throw e
      }
    },
    [lessonId, handleNoteEditingHeartbeat423]
  )

  const handleNoteEditingPayload = useCallback((payload: LessonNoteEditingPayload) => {
    setNoteEditingRemote({
      shared: payload.shared,
      private_teacher: payload.private_teacher,
    })
    // Live-sync черновика (даже до сохранения), пока редактирует другой участник.
    if (!holdingSharedRef.current && payload.shared?.user_id !== authUserId && payload.shared_draft) {
      const d = payload.shared_draft as EditorJsData
      // Пустой/начальный payload при acquire не должен затирать уже показанный черновик у наблюдателей.
      if (!editorJsDataIsEffectivelyEmpty(d)) {
        setIsSharedDirty(true)
        setSharedDraft(d)
      }
    }
    if (
      !holdingPrivateRef.current &&
      payload.private_teacher?.user_id !== authUserId &&
      payload.private_teacher_draft
    ) {
      const d = payload.private_teacher_draft as EditorJsData
      if (!editorJsDataIsEffectivelyEmpty(d)) {
        setIsPrivateTeacherDirty(true)
        setPrivateTeacherDraft(d)
      }
    }
  }, [authUserId])

  const remoteLockShared = useMemo(() => {
    const r = noteEditingRemote.shared
    if (!r || !authUserId) return null
    if (r.user_id === authUserId) return null
    return r
  }, [noteEditingRemote.shared, authUserId])

  const remoteLockPrivate = useMemo(() => {
    const r = noteEditingRemote.private_teacher
    if (!r || !authUserId) return null
    if (r.user_id === authUserId) return null
    return r
  }, [noteEditingRemote.private_teacher, authUserId])

  const prevRemoteLockSharedRef = useRef<typeof remoteLockShared>(null)
  const prevRemoteLockPrivateRef = useRef<typeof remoteLockPrivate>(null)

  const readOnlySharedEffective = useMemo(
    () => !canEditSharedNotes || Boolean(remoteLockShared),
    [canEditSharedNotes, remoteLockShared]
  )

  const readOnlyPrivateEffective = useMemo(
    () => !canEditPrivateTeacherNotes || Boolean(remoteLockPrivate),
    [canEditPrivateTeacherNotes, remoteLockPrivate]
  )

  const notesByVisibility = useMemo(() => {
    const priv = notes.filter((n) => n.visibility === 'private_teacher')
    const shared = notes.filter((n) => n.visibility === 'shared')
    return { priv, shared }
  }, [notes])

  const latestNoteByVisibility = useMemo(() => {
    const pickLatest = (v: LessonNoteVisibility): LessonNote | null => {
      const list = notesByVisibility[v === 'private_teacher' ? 'priv' : 'shared']
      if (list.length === 0) return null
      const sorted = [...list].sort(
        (a, b) =>
          Math.max(toTs(b.updated_at), toTs(b.created_at)) -
          Math.max(toTs(a.updated_at), toTs(a.created_at))
      )
      return sorted[0] ?? null
    }
    return {
      private_teacher: pickLatest('private_teacher'),
      shared: pickLatest('shared'),
    }
  }, [notesByVisibility])

  const refreshNotesOnly = useCallback(async () => {
    if (!Number.isFinite(lessonId)) return
    const notesData = await lessonNotesApi.getNotes(lessonId)
    setNotes(notesData)
  }, [lessonId])

  const fetchAll = useCallback(async () => {
    if (!Number.isFinite(lessonId)) return
    setLoading(true)
    setError(null)
    try {
      const [lessonData, notesData, previousNoteData] = await Promise.all([
        lessonsApi.getById(lessonId),
        lessonNotesApi.getNotes(lessonId),
        lessonNotesApi.getPreviousNote(lessonId),
      ])
      setLesson(lessonData)
      setNotes(notesData)
      setPreviousNote(previousNoteData)
      setParticipants(lessonData.participants ?? [])
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('lessons.loadDetailsError'))
    } finally {
      setLoading(false)
    }
  }, [lessonId, t])

  useEffect(() => {
    fetchAll().catch(() => {})
  }, [fetchAll])

  const fetchLessonHomeworks = useCallback(async () => {
    if (!Number.isFinite(lessonId)) return
    if (!lesson || lesson.status !== 'completed') return
    if (!authUserId && !isLessonTeacher) return

    setLessonHomeworksLoading(true)
    setLessonHomeworksError(null)
    try {
      const studentIds =
        (lesson.participants ?? [])
          .map((p) => p.user_id)
          .filter((id): id is number => Number.isFinite(id)) ?? []

      // Учитель по умолчанию видит ДЗ для всех учеников урока (из списка участников урока).
      if (isLessonTeacher) {
        if (studentIds.length > 0) {
          const all = (await Promise.all(studentIds.map((sid) => homeworksApi.getByUser(sid)))).flat()
          const filtered = all.filter((h) => h.lesson_id === lessonId)

          // Дедупликация на случай повторов из-за API/данных.
          const byId = new Map<number, Homework>()
          for (const hw of filtered) byId.set(hw.id, hw)

          setLessonHomeworks(Array.from(byId.values()))
          return
        }

        // Fallback, если participants не пришли (или список пуст): пробуем по recipient_id.
        if (Number.isFinite(lesson.recipient_id)) {
          const source =
            lesson.lesson_scope === 'group'
              ? await homeworksApi.getByGroup(lesson.recipient_id)
              : await homeworksApi.getByUser(lesson.recipient_id)
          setLessonHomeworks(source.filter((h) => h.lesson_id === lessonId))
          return
        }
      }

      // Для ученика: домашки только текущего пользователя и фильтр по lesson_id.
      if (authUserId) {
        const source = await homeworksApi.getByUser(authUserId)
        setLessonHomeworks(source.filter((h) => h.lesson_id === lessonId))
      } else {
        setLessonHomeworks([])
      }
    } catch (e: unknown) {
      setLessonHomeworksError(getErrorMessage(e) || t('lessons.homeworksLoadError'))
      setLessonHomeworks([])
    } finally {
      setLessonHomeworksLoading(false)
    }
  }, [lessonId, lesson, authUserId, isLessonTeacher, t])

  const fetchLessonTests = useCallback(async () => {
    if (!Number.isFinite(lessonId)) return

    setLessonTestsLoading(true)
    setLessonTestsError(null)
    try {
      const res = await testsApi.list({ lesson_id: lessonId, per_page: 100 })
      setLessonTests(res.data ?? [])
    } catch (e: unknown) {
      setLessonTestsError(getErrorMessage(e) || t('lessons.testsLoadError'))
      setLessonTests([])
    } finally {
      setLessonTestsLoading(false)
    }
  }, [lessonId, t])

    const fetchLessonWords = useCallback(async () => {
    if (!Number.isFinite(lessonId)) return

    setLessonWordsLoading(true)
    setLessonWordsError(null)
    try {
      const items = await lessonWordsApi.list(lessonId)
      setLessonWords(items)
    } catch (e: unknown) {
      setLessonWordsError(getErrorMessage(e) || t('lessonWords.loadError'))
      setLessonWords([])
    } finally {
      setLessonWordsLoading(false)
    }
  }, [lessonId, t])

  useEffect(() => {
    const status = lesson?.status
    if (!status) return
    if (status !== 'completed') {
      setLessonHomeworks([])
      setLessonHomeworksError(null)
      return
    }
    fetchLessonHomeworks().catch(() => {})
  }, [lesson?.status, fetchLessonHomeworks])

  useEffect(() => {
    if (!Number.isFinite(lessonId) || loading) return
    fetchLessonTests().catch(() => {})
  }, [lessonId, loading, fetchLessonTests])

  useEffect(() => {
    if (!Number.isFinite(lessonId) || loading) return
    fetchLessonWords().catch(() => {})
  }, [lessonId, loading, fetchLessonWords])

  const handleReloadNotes = useCallback(async () => {
    setIsSharedDirty(false)
    setIsPrivateTeacherDirty(false)
    setIsNoteRealtimeConflict(false)
    setSharedNoteSaveError(null)
    setPrivateTeacherNoteSaveError(null)
    await fetchAll()
  }, [fetchAll])

  const handleLessonRealtimeUpdate = useCallback(
    (type: LessonRealtimeUpdateType) => {
      const isAnyNoteDirty = isSharedDirty || isPrivateTeacherDirty
      if (type === 'note' && (canEditSharedNotes || canEditPrivateTeacherNotes) && isAnyNoteDirty) {
        setIsNoteRealtimeConflict(true)
        return
      }
      if (type === 'homework') {
        fetchLessonHomeworks().catch(() => {})
        return
      }
      if (type === 'word') {
        fetchLessonWords().catch(() => {})
        return
      }
      fetchAll().catch(() => {})
    },
    [fetchAll, fetchLessonHomeworks, fetchLessonWords, canEditSharedNotes, canEditPrivateTeacherNotes, isSharedDirty, isPrivateTeacherDirty]
  )

  const handleSharedNoteEditorFocus = useCallback(async () => {
    if (!canEditSharedNotes || !Number.isFinite(lessonId)) return
    try {
      const data = await lessonNotesApi.setNoteEditing(lessonId, 'shared', true, sharedDraft)
      setNoteEditingRemote({
        shared: data.shared ?? null,
        private_teacher: data.private_teacher ?? null,
      })
      if (data.acquired !== false) {
        holdingSharedRef.current = true
        setEditingSharedFocus(true)
      }
    } catch {
      /* ignore */
    }
  }, [canEditSharedNotes, lessonId, sharedDraft])

  const handleSharedNoteEditorBlur = useCallback(async () => {
    if (!Number.isFinite(lessonId)) return
    if (!holdingSharedRef.current) return
    holdingSharedRef.current = false
    setEditingSharedFocus(false)
    try {
      const data = await lessonNotesApi.setNoteEditing(lessonId, 'shared', false)
      setNoteEditingRemote({
        shared: data.shared ?? null,
        private_teacher: data.private_teacher ?? null,
      })
    } catch {
      /* ignore */
    }
  }, [lessonId])

  const handlePrivateNoteEditorFocus = useCallback(async () => {
    if (!canEditPrivateTeacherNotes || !Number.isFinite(lessonId) || !isLessonTeacher) return
    try {
      const data = await lessonNotesApi.setNoteEditing(lessonId, 'private_teacher', true, privateTeacherDraft)
      setNoteEditingRemote({
        shared: data.shared ?? null,
        private_teacher: data.private_teacher ?? null,
      })
      if (data.acquired !== false) {
        holdingPrivateRef.current = true
        setEditingPrivateFocus(true)
      }
    } catch {
      /* ignore */
    }
  }, [canEditPrivateTeacherNotes, lessonId, isLessonTeacher, privateTeacherDraft])

  const handlePrivateNoteEditorBlur = useCallback(async () => {
    if (!Number.isFinite(lessonId)) return
    if (!holdingPrivateRef.current) return
    holdingPrivateRef.current = false
    setEditingPrivateFocus(false)
    try {
      const data = await lessonNotesApi.setNoteEditing(lessonId, 'private_teacher', false)
      setNoteEditingRemote({
        shared: data.shared ?? null,
        private_teacher: data.private_teacher ?? null,
      })
    } catch {
      /* ignore */
    }
  }, [lessonId])

  useEffect(() => {
    if (!Number.isFinite(lessonId)) return
    lessonNotesApi.getNoteEditingState(lessonId).then(setNoteEditingRemote).catch(() => {})
  }, [lessonId])

  useEffect(() => {
    if (!Number.isFinite(lessonId) || !editingSharedFocus || !canEditSharedNotes) return
    const tick = window.setInterval(() => {
      void pulseNoteEditingHeartbeat('shared', sharedDraft).catch(() => {})
    }, 20000)
    return () => clearInterval(tick)
  }, [lessonId, editingSharedFocus, canEditSharedNotes, pulseNoteEditingHeartbeat, sharedDraft])

  useEffect(() => {
    if (!Number.isFinite(lessonId) || !editingPrivateFocus || !canEditPrivateTeacherNotes) return
    const tick = window.setInterval(() => {
      void pulseNoteEditingHeartbeat('private_teacher', privateTeacherDraft).catch(() => {})
    }, 20000)
    return () => clearInterval(tick)
  }, [lessonId, editingPrivateFocus, canEditPrivateTeacherNotes, pulseNoteEditingHeartbeat, privateTeacherDraft])

  useEffect(() => {
    if (!Number.isFinite(lessonId) || !editingSharedFocus || !holdingSharedRef.current) return
    if (sharedLiveSyncTimerRef.current != null) window.clearTimeout(sharedLiveSyncTimerRef.current)
    sharedLiveSyncTimerRef.current = window.setTimeout(() => {
      void pulseNoteEditingHeartbeat('shared', sharedDraft).catch(() => {})
    }, 700)

    return () => {
      if (sharedLiveSyncTimerRef.current != null) {
        window.clearTimeout(sharedLiveSyncTimerRef.current)
        sharedLiveSyncTimerRef.current = null
      }
    }
  }, [lessonId, editingSharedFocus, pulseNoteEditingHeartbeat, sharedDraft])

  useEffect(() => {
    if (!Number.isFinite(lessonId) || !editingPrivateFocus || !holdingPrivateRef.current) return
    if (privateLiveSyncTimerRef.current != null) window.clearTimeout(privateLiveSyncTimerRef.current)
    privateLiveSyncTimerRef.current = window.setTimeout(() => {
      void pulseNoteEditingHeartbeat('private_teacher', privateTeacherDraft).catch(() => {})
    }, 700)

    return () => {
      if (privateLiveSyncTimerRef.current != null) {
        window.clearTimeout(privateLiveSyncTimerRef.current)
        privateLiveSyncTimerRef.current = null
      }
    }
  }, [lessonId, editingPrivateFocus, pulseNoteEditingHeartbeat, privateTeacherDraft])

  useEffect(() => {
    const lid = lessonId
    return () => {
      if (!Number.isFinite(lid)) return
      if (holdingSharedRef.current) {
        holdingSharedRef.current = false
        void lessonNotesApi.setNoteEditing(lid, 'shared', false)
      }
      if (holdingPrivateRef.current) {
        holdingPrivateRef.current = false
        void lessonNotesApi.setNoteEditing(lid, 'private_teacher', false)
      }
    }
  }, [lessonId])

  useEffect(() => {
    if ((canEditSharedNotes || canEditPrivateTeacherNotes) || !Number.isFinite(lessonId)) return
    if (holdingSharedRef.current) {
      holdingSharedRef.current = false
      setEditingSharedFocus(false)
      void lessonNotesApi.setNoteEditing(lessonId, 'shared', false)
    }
    if (holdingPrivateRef.current) {
      holdingPrivateRef.current = false
      setEditingPrivateFocus(false)
      void lessonNotesApi.setNoteEditing(lessonId, 'private_teacher', false)
    }
  }, [canEditSharedNotes, canEditPrivateTeacherNotes, lessonId])

  useEffect(() => {
    const hadLock = Boolean(prevRemoteLockSharedRef.current)
    const hasLockNow = Boolean(remoteLockShared)
    const lockReleased = hadLock && !hasLockNow
    prevRemoteLockSharedRef.current = remoteLockShared

    if (!lockReleased) return
    // Если пользователь сам удерживает лок — не трогаем его редактор.
    if (holdingSharedRef.current) return

    // Не сбрасываем dirty: в БД может быть старый снимок, а чужой несохранённый текст уже в sharedDraft.
    refreshNotesOnly().catch(() => {})
  }, [remoteLockShared, refreshNotesOnly])

  useEffect(() => {
    const hadLock = Boolean(prevRemoteLockPrivateRef.current)
    const hasLockNow = Boolean(remoteLockPrivate)
    const lockReleased = hadLock && !hasLockNow
    prevRemoteLockPrivateRef.current = remoteLockPrivate

    if (!lockReleased) return
    if (holdingPrivateRef.current) return

    refreshNotesOnly().catch(() => {})
  }, [remoteLockPrivate, refreshNotesOnly])

  const { presentUsers } = useLessonRealtime(Number.isFinite(lessonId) ? lessonId : null, handleLessonRealtimeUpdate, {
    onNoteEditing: handleNoteEditingPayload,
    authUserId,
  })

  const openPostFinishWizard = useCallback(() => {
    setHomeworkDraft(emptyEditorJs)
    setHomeworkSaveError(null)
    setHomeworkEditorNonce((n) => n + 1)
    setIsSharedNotesOpen(false)
    setIsPrivateNotesOpen(false)
    setPostFinishWizardOpen(true)
  }, [])

  const openPostFinishFlow = useCallback(
    (_finishedLesson: Lesson, openHomeworkModal = true) => {
      if (!isLessonTeacher) return
      if (openHomeworkModal) {
        openPostFinishWizard()
      }
    },
    [isLessonTeacher, openPostFinishWizard]
  )

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('after_finish') !== '1' || !lesson || loading) return
    openPostFinishFlow(lesson, true)
    window.history.replaceState({}, '', ROUTES.LESSON_DETAILS(lessonId))
  }, [lesson, loading, lessonId, openPostFinishFlow])

  useEffect(() => {
    // Пока есть несохранённое в редакторе (в т.ч. после blur или после live-sync чужого черновика) — не затирать из БД.
    if (isSharedDirty) return
    const latest = latestNoteByVisibility.shared
    setSharedDraft(latest?.body_json ?? emptyEditorJs)
    setSharedNoteSaveError(null)
  }, [latestNoteByVisibility.shared, isSharedDirty])

  useEffect(() => {
    if (!isLessonTeacher) return
    if (isPrivateTeacherDirty) return
    const latest = latestNoteByVisibility.private_teacher
    setPrivateTeacherDraft(latest?.body_json ?? emptyEditorJs)
    setPrivateTeacherNoteSaveError(null)
  }, [latestNoteByVisibility.private_teacher, isLessonTeacher, isPrivateTeacherDirty])

  useEffect(() => {
    if (!lesson) return
    // Любые состояния, кроме in_progress, не должны оставлять dirty-режим.
    if (lesson.status === 'in_progress') return
    setIsSharedDirty(false)
    setIsPrivateTeacherDirty(false)
    setIsNoteRealtimeConflict(false)
  }, [lesson])

  useEffect(() => {
    if (!lesson) return
    const needsTick =
      (lesson.status === 'in_progress' && lesson.actual_start_at) ||
      ((lesson.status === 'planned' || lesson.status === 'in_progress') &&
        lesson.planned_start_at &&
        lesson.planned_end_at)

    if (!needsTick) return

    const intervalId = window.setInterval(() => setNowTick(Date.now()), 1000)
    return () => window.clearInterval(intervalId)
  }, [lesson])

  const handleStart = async () => {
    if (!lessonId) return
    try {
      await lessonsApi.start(lessonId)
      await fetchAll()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('lessons.startError'))
    }
  }

  const handleFinish = async () => {
    if (!lessonId) return
    try {
      const result = await lessonsApi.finish(lessonId)
      await fetchAll()
      openPostFinishFlow(result.lesson, result.openHomeworkModal)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('lessons.finishError'))
    }
  }

  const handleCancel = async () => {
    if (!lessonId) return
    if (!window.confirm(t('lessons.cancelConfirm', 'Отменить урок?'))) return
    try {
      await lessonsApi.cancel(lessonId)
      await fetchAll()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('lessons.cancelError'))
    }
  }

  const handleSaveSharedNote = async () => {
    setSharedNoteSaveError(null)
    try {
      const latest = latestNoteByVisibility.shared
      if (latest) {
        await lessonNotesApi.updateNote(latest.id, { visibility: 'shared', body_json: sharedDraft })
      } else {
        await lessonNotesApi.createNote(lessonId, { visibility: 'shared', body_json: sharedDraft })
      }
      setIsSharedDirty(false)
      setIsNoteRealtimeConflict(false)
      await fetchAll()
    } catch (e: unknown) {
      setSharedNoteSaveError(getErrorMessage(e) || t('lessonNotes.sharedSaveError'))
    }
  }

  const handleSavePrivateTeacherNote = async () => {
    setPrivateTeacherNoteSaveError(null)
    try {
      const latest = latestNoteByVisibility.private_teacher
      if (latest) {
        await lessonNotesApi.updateNote(latest.id, { visibility: 'private_teacher', body_json: privateTeacherDraft })
      } else {
        await lessonNotesApi.createNote(lessonId, { visibility: 'private_teacher', body_json: privateTeacherDraft })
      }
      setIsPrivateTeacherDirty(false)
      setIsNoteRealtimeConflict(false)
      await fetchAll()
    } catch (e: unknown) {
      setPrivateTeacherNoteSaveError(getErrorMessage(e) || t('lessonNotes.privateTeacherSaveError'))
    }
  }

  const handleSaveHomework = async () => {
    if (!lessonId) return
    setSubmittingHomework(true)
    setHomeworkSaveError(null)
    try {
      // Backend валидирует `task_json.blocks` как array.
      // Иногда Editor.js может отдавать пустой blocks, поэтому гарантируем минимальный paragraph.
      const safeTaskJson: EditorJsData =
        !Array.isArray(homeworkDraft.blocks) || homeworkDraft.blocks.length === 0
          ? { ...homeworkDraft, version: homeworkDraft.version ?? '2.0.0', blocks: [{ type: 'paragraph', data: { text: '' } }] }
          : homeworkDraft

      await homeworksApi.createFromLesson(lessonId, { task_json: safeTaskJson })
      await fetchLessonHomeworks()
      setPostFinishWizardOpen(false)
    } catch (e: unknown) {
      setHomeworkSaveError(getErrorMessage(e) || t('homeworks.saveError'))
    } finally {
      setSubmittingHomework(false)
    }
  }

  const openHomeworkViewModal = (hw: Homework) => {
    setHomeworkToView(hw)
    setHomeworkViewModalOpen(true)
  }

  const lessonStatusLabel = (s: LessonStatus | string) => {
    if (s === 'planned') return t('lessons.status.planned', 'planned')
    if (s === 'in_progress') return t('lessons.status.in_progress', 'in progress')
    if (s === 'completed') return t('lessons.status.completed', 'completed')
    if (s === 'canceled') return t('lessons.status.canceled', 'canceled')
    return String(s)
  }

  const lessonStartAt = lesson?.actual_start_at ?? lesson?.planned_start_at ?? null
  const lessonEndAt = lesson?.actual_end_at ?? null

  const lessonStartLabel = lessonStartAt
    ? formatLessonTimeRange(lessonStartAt, lesson?.planned_end_at ?? lesson?.actual_end_at ?? null)
    : '—'

  const formatDurationLabel = (ms: number): string => {
    const totalSeconds = Math.floor(Math.abs(ms) / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${hours > 0 ? `${pad(hours)}:` : ''}${pad(minutes)}:${pad(seconds)}`
  }

  const plannedTimer = useMemo(() => {
    if (!lesson?.planned_start_at || !lesson.planned_end_at) return null
    if (lesson.status !== 'planned') return null

    const startMs = new Date(lesson.planned_start_at).getTime()
    const endMs = new Date(lesson.planned_end_at).getTime()
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null

    if (nowTick < startMs) {
      return {
        prefix: t('lessons.plannedTimer.beforeStart', 'До начала'),
        ms: startMs - nowTick,
      }
    }

    if (nowTick <= endMs) {
      return {
        prefix: t('lessons.plannedTimer.remaining', 'Осталось'),
        ms: endMs - nowTick,
      }
    }

    return {
      prefix: t('lessons.plannedTimer.overdue', 'Плановое время вышло +'),
      ms: nowTick - endMs,
    }
  }, [lesson?.planned_end_at, lesson?.planned_start_at, lesson?.status, nowTick, t])

  const plannedTimerLabel = plannedTimer
    ? `${plannedTimer.prefix} ${formatDurationLabel(plannedTimer.ms)}`
    : null

  const timerMs = useMemo(() => {
    if (!lessonStartAt) return null

    const startMs = new Date(lessonStartAt).getTime()
    if (!Number.isFinite(startMs)) return null

    if (lesson?.status === 'in_progress') {
      return Math.max(0, nowTick - startMs)
    }

    if (lesson?.status === 'completed' && lessonEndAt) {
      const endMs = new Date(lessonEndAt).getTime()
      if (!Number.isFinite(endMs)) return null
      return Math.max(0, endMs - startMs)
    }

    return null
  }, [lessonStartAt, lessonEndAt, lesson?.status, nowTick])

  const timerLabel = useMemo(() => {
    if (timerMs == null) return '—'
    return formatDurationLabel(timerMs)
  }, [timerMs])

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 }, pb: 3, display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 120px)' }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Link component={RouterLink} to={lessonsListPath} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            {t('lessons.title', 'Уроки')}
          </Link>
          <Typography color="text.primary">
            {t('lessons.detailsTitle', 'Урок #{id}', { id: Number.isFinite(lessonId) ? lessonId : '—' })}
          </Typography>
        </Breadcrumbs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {Number.isFinite(lessonId) && <LessonStartSoonBanner lessonId={lessonId} />}

        {lesson && (
          <LessonPaymentBanner
            lesson={lesson}
            isTeacher={isLessonTeacher}
            onOpenPaymentModal={() => openPostFinishWizard()}
          />
        )}

        {loading || !lesson ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 1) Карточка заголовка (title + кнопки) */}
            <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />

            {/* 2..5) 4 крупных блока: заметки (2) + участники + детали */}
            <Box sx={{ display: 'flex', flex: 1, flexDirection: { xs: 'column', lg: 'row' }, gap: 2, minHeight: 0 }}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Skeleton variant="rectangular" height={230} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" height={230} sx={{ borderRadius: 2 }} />
              </Box>

              <Box sx={{ width: { xs: '100%', lg: 360 }, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
                <Skeleton variant="rectangular" height={190} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" height={190} sx={{ borderRadius: 2 }} />
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <Card sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h4" component="div" fontWeight={700}>
                    {t('lessons.detailsTitle', 'Урок #{id}', { id: lessonId })}
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {isLessonTeacher && lesson.status === 'planned' && (
                    <Button startIcon={<PlayArrowIcon />} variant="contained" size="small" color="secondary" onClick={handleStart}>
                      {t('lessons.start', 'Начать')}
                    </Button>
                  )}
                  {isLessonTeacher && lesson && canRescheduleLesson(lesson) && (
                    <Button
                      startIcon={<EventIcon />}
                      variant="outlined"
                      size="small"
                      color="secondary"
                      onClick={() => setRescheduleModalOpen(true)}
                    >
                      {t('lessons.reschedule.button', 'Перенести')}
                    </Button>
                  )}
                  {isLessonTeacher && lesson.status === 'in_progress' && (
                    <Button startIcon={<StopIcon />} variant="outlined" size="small" color="secondary" onClick={handleFinish}>
                      {t('lessons.finish', 'Завершить')}
                    </Button>
                  )}
                  {isLessonTeacher && (lesson.status === 'planned' || lesson.status === 'in_progress') && (
                    <Button startIcon={<CancelIcon />} variant="outlined" size="small" color="secondary" onClick={handleCancel}>
                      {t('lessons.cancel', 'Отменить')}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', flex: 1, flexDirection: { xs: 'column', lg: 'row' }, gap: 2, minHeight: 0 }}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <LessonWordsPanel
                  lessonId={lessonId}
                  lessonStatus={lesson.status}
                  participants={participants}
                  isLessonTeacher={isLessonTeacher}
                  words={lessonWords}
                  loading={lessonWordsLoading}
                  error={lessonWordsError}
                  onRefresh={fetchLessonWords}
                />

                {(canEditSharedNotes || canEditPrivateTeacherNotes) && isNoteRealtimeConflict && (
                  <Alert
                    severity="warning"
                    sx={{ my: 1 }}
                    action={
                      <Button size="small" variant="outlined" color="secondary" onClick={() => void handleReloadNotes()}>
                        {t('lessonNotes.realtimeMergeReload', 'Обновить')}
                      </Button>
                    }
                  >
                    {t(
                      'lessonNotes.realtimeConflict',
                      'Пришли внешние изменения. Чтобы увидеть их, обновите список и перезагрузите редактор (несохранённые правки будут потеряны).'
                    )}
                  </Alert>
                )}

                {noteEditingExpiredMessage && (
                  <Alert
                    severity="warning"
                    sx={{ my: 1 }}
                    onClose={() => setNoteEditingExpiredMessage(null)}
                  >
                    {noteEditingExpiredMessage}
                  </Alert>
                )}

                <Card
                  sx={
                    [
                      { borderRadius: 2 },
                      notesMutedByLessonStatus
                        ? lessonNoteCardMutedSx
                        : { bgcolor: canEditSharedNotes ? 'background.paper' : 'background.default' },
                    ] as SxProps<Theme>
                  }
                >
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditNoteIcon
                          fontSize="small"
                          sx={notesMutedByLessonStatus ? lessonNoteIconMutedSx : { color: 'primary.main' }}
                        />
                        <Typography
                          variant="h6"
                          component="div"
                          fontWeight={800}
                          sx={
                            [
                              { lineHeight: 1.2 },
                              notesMutedByLessonStatus ? lessonNoteTitleMutedSx : { color: 'text.primary' },
                            ] as SxProps<Theme>
                          }
                        >
                          {t('lessonNotes.shared', 'Общие заметки')}
                        </Typography>
                        <Tooltip
                          title={t(
                            'lessonNotes.sharedDescription',
                            'Заметки, видимые учителю и ученику. Редактируются в ходе урока и сохраняются в истории.',
                          )}
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={notesMutedByLessonStatus ? lessonNoteIconMutedSx : { color: 'text.secondary' }}
                          />
                        </Tooltip>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => setIsSharedNotesOpen((v) => !v)}
                        aria-label={isSharedNotesOpen ? 'Свернуть общую заметку' : 'Развернуть общую заметку'}
                        sx={notesMutedByLessonStatus ? lessonNoteCollapseBtnMutedSx : undefined}
                      >
                        {isSharedNotesOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>

                    <Collapse in={isSharedNotesOpen} timeout="auto" collapsedSize={0} unmountOnExit>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={notesMutedByLessonStatus ? lessonNoteCaptionMutedSx : { color: 'text.secondary' }}
                        >
                          {t('lessonNotes.sharedCaption', 'Видна учителю и ученику')}
                        </Typography>

                        {previousNote && (
                          <PreviousNoteSection
                            previousNote={previousNote}
                            visibility="shared"
                            muted={notesMutedByLessonStatus}
                            t={t}
                          />
                        )}

                        <Tooltip
                          title={
                            remoteLockShared
                              ? t('lessonNotes.editingBy', 'Редактирует: {{name}}', {
                                  name: remoteLockShared.display_name,
                                })
                              : ''
                          }
                          disableHoverListener={!remoteLockShared}
                          followCursor
                        >
                          <Box
                            component="span"
                            display="block"
                            sx={{
                              position: 'relative',
                              cursor: readOnlySharedEffective ? 'not-allowed' : 'text',
                              '&:hover .lessonNoteLockOverlay': { opacity: readOnlySharedEffective ? 1 : 0 },
                            }}
                          >
                            <EditorJsField
                              key={`lesson-${lessonId}-note-shared`}
                              value={sharedDraft}
                              onChange={(next) => {
                                setSharedDraft(next)
                                if (canEditSharedNotes) setIsSharedDirty(true)
                                setIsNoteRealtimeConflict(false)
                              }}
                              readOnly={readOnlySharedEffective}
                              readOnlySurface="paper"
                              hideLabel
                              rows={4}
                              onFocus={handleSharedNoteEditorFocus}
                              onBlur={handleSharedNoteEditorBlur}
                              holderSx={
                                notesMutedByLessonStatus
                                  ? lessonNoteEditorHolderMutedSx
                                  : readOnlySharedEffective
                                    ? { backgroundColor: 'background.paper' }
                                    : undefined
                              }
                            />
                            {readOnlySharedEffective && (
                              <Box
                                className="lessonNoteLockOverlay"
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: (theme) => withAlpha(theme.palette.background.paper, 0.65),
                                  opacity: 0,
                                  transition: 'opacity 150ms ease',
                                  pointerEvents: 'none',
                                }}
                              >
                                <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              </Box>
                            )}
                          </Box>
                        </Tooltip>

                        {sharedNoteSaveError && (
                          <Typography color="error.main" variant="body2">
                            {sharedNoteSaveError}
                          </Typography>
                        )}

                        {canEditSharedNotes && !remoteLockShared && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              color="secondary"
                              onClick={() => void handleSaveSharedNote()}
                            >
                              {t('common.save', 'Сохранить')}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>

                {isLessonTeacher && (
                  <Card
                    sx={
                      [
                        { borderRadius: 2 },
                        notesMutedByLessonStatus
                          ? lessonNoteCardMutedSx
                          : { bgcolor: canEditPrivateTeacherNotes ? 'background.paper' : 'background.default' },
                      ] as SxProps<Theme>
                    }
                  >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LockIcon
                            fontSize="small"
                            sx={notesMutedByLessonStatus ? lessonNoteIconMutedSx : { color: 'text.secondary' }}
                          />
                          <Typography
                            variant="h6"
                            component="div"
                            fontWeight={800}
                            sx={
                              [
                                { lineHeight: 1.2 },
                                notesMutedByLessonStatus ? lessonNoteTitleMutedSx : { color: 'text.primary' },
                              ] as SxProps<Theme>
                            }
                          >
                            {t('lessonNotes.privateTeacher', 'Приватные заметки учителя')}
                          </Typography>
                          <Tooltip
                            title={t(
                              'lessonNotes.privateTeacherDescription',
                              'Заметки только для учителя. Ученик их не видит.',
                            )}
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={notesMutedByLessonStatus ? lessonNoteIconMutedSx : { color: 'text.secondary' }}
                            />
                          </Tooltip>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => setIsPrivateNotesOpen((v) => !v)}
                          aria-label={isPrivateNotesOpen ? 'Свернуть приватную заметку учителя' : 'Развернуть приватную заметку учителя'}
                          sx={notesMutedByLessonStatus ? lessonNoteCollapseBtnMutedSx : undefined}
                        >
                          {isPrivateNotesOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Box>

                      <Collapse in={isPrivateNotesOpen} timeout="auto" collapsedSize={0} unmountOnExit>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={notesMutedByLessonStatus ? lessonNoteCaptionMutedSx : { color: 'text.secondary' }}
                          >
                            {t('lessonNotes.privateTeacherCaption', 'Видна только учителю')}
                          </Typography>

                          {previousNote && (
                            <PreviousNoteSection
                              previousNote={previousNote}
                              visibility="private_teacher"
                              muted={notesMutedByLessonStatus}
                              t={t}
                            />
                          )}

                          <Tooltip
                            title={
                              remoteLockPrivate
                                ? t('lessonNotes.editingBy', 'Редактирует: {{name}}', {
                                    name: remoteLockPrivate.display_name,
                                  })
                                : ''
                            }
                            disableHoverListener={!remoteLockPrivate}
                            followCursor
                          >
                            <Box
                              component="span"
                              display="block"
                              sx={{
                                position: 'relative',
                                cursor: readOnlyPrivateEffective ? 'not-allowed' : 'text',
                                '&:hover .lessonNoteLockOverlay': { opacity: readOnlyPrivateEffective ? 1 : 0 },
                              }}
                            >
                              <EditorJsField
                                key={`lesson-${lessonId}-note-private`}
                                value={privateTeacherDraft}
                                onChange={(next) => {
                                  setPrivateTeacherDraft(next)
                                  if (canEditPrivateTeacherNotes) setIsPrivateTeacherDirty(true)
                                  setIsNoteRealtimeConflict(false)
                                }}
                                readOnly={readOnlyPrivateEffective}
                                readOnlySurface="paper"
                                hideLabel
                                rows={4}
                                onFocus={handlePrivateNoteEditorFocus}
                                onBlur={handlePrivateNoteEditorBlur}
                                holderSx={
                                  notesMutedByLessonStatus
                                    ? lessonNoteEditorHolderMutedSx
                                    : readOnlyPrivateEffective
                                      ? { backgroundColor: 'background.paper' }
                                      : undefined
                                }
                              />
                              {readOnlyPrivateEffective && (
                                <Box
                                  className="lessonNoteLockOverlay"
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: (theme) => withAlpha(theme.palette.background.paper, 0.65),
                                    opacity: 0,
                                    transition: 'opacity 150ms ease',
                                    pointerEvents: 'none',
                                  }}
                                >
                                  <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                </Box>
                              )}
                            </Box>
                          </Tooltip>

                          {privateTeacherNoteSaveError && (
                            <Typography color="error.main" variant="body2">
                              {privateTeacherNoteSaveError}
                            </Typography>
                          )}

                          {canEditPrivateTeacherNotes && !remoteLockPrivate && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              color="secondary"
                              onClick={() => void handleSavePrivateTeacherNote()}
                            >
                              {t('common.save', 'Сохранить')}
                            </Button>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                )}
              </Box>

              <Box sx={{ width: { xs: '100%', lg: 360 }, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
                <Card sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" component="div" fontWeight={700}>
                        {t('lessonParticipants.title', 'Участники')}
                      </Typography>
                      <Chip
                        size="small"
                        label={participants.length}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 700,
                          bgcolor: (theme) => withAlpha(theme.palette.primary.main, 0.14),
                          color: 'primary.main',
                        }}
                      />
                    </Box>

                    {participants.length === 0 ? (
                      <Typography color="text.secondary">{t('lessonParticipants.empty', 'Нет участников')}</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0, overflow: 'auto' }}>
                        {participants.map((p) => {
                          const name =
                            p.user?.display_name ??
                            [p.user?.surname, p.user?.first_name].filter(Boolean).join(' ') ??
                            p.user?.email ??
                            `user #${p.user_id}`
                          const isParticipantOnline = presentUsers.some((u) => u.id === p.user_id)
                          return (
                            <Box
                              key={p.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                p: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                                  <Avatar
                                    src={p.user?.avatar_url ?? undefined}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: (theme) => withAlpha(theme.palette.primary.main, 0.14),
                                      color: 'primary.main',
                                      fontWeight: 700,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {(name || '#').slice(0, 1).toUpperCase()}
                                  </Avatar>
                                  {/* Индикатор как у Tailwind: w-2.5 h-2.5 rounded-full bg-green-500 / bg-gray-300 */}
                                  <Box
                                    component="span"
                                    sx={{
                                      position: 'absolute',
                                      bottom: 0,
                                      right: 0,
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      bgcolor: isParticipantOnline ? '#22c55e' : '#d1d5db',
                                      border: '2px solid',
                                      borderColor: 'background.paper',
                                      boxSizing: 'border-box',
                                    }}
                                  />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
                                    {name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                                    {t('dashboard.student', 'Ученик')}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )
                        })}
                      </Box>
                    )}
                  </CardContent>
                </Card>

                <Card sx={{ minHeight: 0 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6" component="div" fontWeight={700}>
                      {t('lessonDetails.metaTitle', 'Детали урока')}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography color="text.secondary">{t('lessons.tableScope', 'Формат')}</Typography>
                      <Typography fontWeight={700}>
                        {lesson.lesson_scope === 'individual'
                          ? t('lessons.scopeIndividual', 'Индивидуальный')
                          : t('lessons.scopeGroup', 'Групповой')}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography color="text.secondary">{t('lessons.tableStatus', 'Статус')}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                        <Chip
                          size="small"
                          label={lessonStatusLabel(lesson.status)}
                          sx={{
                            borderRadius: 999,
                            fontWeight: 700,
                            ...(lesson.status === 'completed'
                              ? { bgcolor: (theme) => withAlpha(theme.palette.success.main, 0.14), color: 'success.main' }
                              : lesson.status === 'in_progress'
                                ? { bgcolor: (theme) => withAlpha(theme.palette.success.main, 0.14), color: 'success.main' }
                                : lesson.status === 'planned'
                                  ? { bgcolor: 'action.disabledBackground', color: 'text.primary' }
                                  : lesson.status === 'canceled'
                                    ? { bgcolor: 'action.disabledBackground', color: 'text.primary' }
                                    : { bgcolor: (theme) => withAlpha(theme.palette.primary.main, 0.14), color: 'primary.main' }),
                          }}
                        />
                        <LessonRescheduledChip lesson={lesson} />
                      </Box>
                    </Box>

                    {lesson.was_rescheduled && (
                      <>
                        {lesson.original_planned_start_at && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography color="text.secondary">
                              {t('lessons.rescheduleOriginalDate', 'Изначально запланирован')}
                            </Typography>
                            <Typography fontWeight={700} textAlign="right">
                              {formatLessonTimeRange(lesson.original_planned_start_at, null)}
                            </Typography>
                          </Box>
                        )}
                        {lesson.last_reschedule_reason?.trim() && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography color="text.secondary">
                              {t('lessons.rescheduleReasonLabel', 'Причина переноса')}
                            </Typography>
                            <Typography fontWeight={700} textAlign="right">
                              {lesson.last_reschedule_reason.trim()}
                            </Typography>
                          </Box>
                        )}
                        {(lesson.reschedule_count ?? 0) > 1 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography color="text.secondary">
                              {t('lessons.rescheduleCount', { count: lesson.reschedule_count })}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography color="text.secondary">{t('lessonDetails.startAt', 'Начало')}</Typography>
                      <Typography fontWeight={700}>{lessonStartLabel}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography color="text.secondary">
                        {lesson.status === 'planned' && plannedTimerLabel
                          ? t('lessons.plannedTimer.label', 'Плановое время')
                          : t('lessons.actualTimer.label', 'Таймер')}
                      </Typography>
                      <Typography fontWeight={700}>
                        {lesson.status === 'planned' ? (plannedTimerLabel ?? '—') : timerLabel}
                      </Typography>
                    </Box>

                    {/* Цена / абонемент — видна только учителю (ученику бэкенд не отдаёт price_*) */}
                    {isLessonTeacher && lesson.subscription_id != null && lesson.subscription_meta ? (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography color="text.secondary">{t('billing.lessonPrice', 'Цена')}</Typography>
                        <Typography fontWeight={700} textAlign="right">
                          {t('billing.subscriptionLessonNumber', '{{number}} из {{total}} занятие по абонементу', {
                            number: lesson.subscription_meta.consumption_number,
                            total: lesson.subscription_meta.lessons_total,
                          })}
                        </Typography>
                      </Box>
                    ) : isLessonTeacher && lesson.price_amount != null ? (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography color="text.secondary">{t('billing.lessonPrice', 'Цена')}</Typography>
                        <Typography fontWeight={700} textAlign="right">
                          {formatMoney(lesson.price_amount, lesson.price_currency ?? 'RUB')} ·{' '}
                          {formatPriceSource(lesson.price_source ?? null, t)}
                        </Typography>
                      </Box>
                    ) : isLessonTeacher &&
                      (lesson.status === 'planned' || lesson.status === 'in_progress') &&
                      lesson.planned_start_at &&
                      lesson.planned_end_at ? (
                      <LessonPricePreview
                        recipientType={lesson.lesson_scope === 'individual' ? 'user' : 'group'}
                        recipientId={lesson.recipient_id}
                        plannedStartAt={lesson.planned_start_at}
                        plannedEndAt={lesson.planned_end_at}
                      />
                    ) : null}

                    {lesson.status === 'completed' && lesson.payment_status && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Typography color="text.secondary">{t('billing.paymentStatusLabel', 'Оплата')}</Typography>
                        <PaymentStatusChip status={lesson.payment_status} />
                      </Box>
                    )}
                  </CardContent>
                </Card>

                <Card sx={{ minHeight: 0 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="h6" component="div" fontWeight={700}>
                        {t('tests.lessonBlock.title', 'Тесты урока')}
                      </Typography>
                      <Chip
                        size="small"
                        label={lessonTests.length}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 700,
                          bgcolor: (theme) => withAlpha(theme.palette.primary.main, 0.14),
                          color: 'primary.main',
                        }}
                      />
                    </Box>

                    {lessonTestsError && (
                      <Alert severity="error" onClose={() => setLessonTestsError(null)}>
                        {lessonTestsError}
                      </Alert>
                    )}

                    {lessonTestsLoading ? (
                      <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {lessonTests.length === 0 ? (
                          <Typography color="text.secondary">{t('tests.lessonBlock.empty', 'Тестов пока нет')}</Typography>
                        ) : (
                          lessonTests.map((tr) => (
                            <Box
                              key={tr.id}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 1.5,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                <Typography variant="body2" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
                                  {t('tests.lessonBlock.testNumber', 'Тест №{{id}}', { id: tr.id })}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={tr.status}
                                  sx={{
                                    borderRadius: 999,
                                    fontWeight: 700,
                                    flexShrink: 0,
                                    ...(tr.status === 'completed'
                                      ? {
                                          bgcolor: (theme) => withAlpha(theme.palette.success.main, 0.14),
                                          color: 'success.main',
                                        }
                                      : tr.status === 'active'
                                        ? {
                                            bgcolor: (theme) => withAlpha(theme.palette.primary.main, 0.14),
                                            color: 'primary.main',
                                          }
                                        : { bgcolor: 'action.disabledBackground', color: 'text.primary' }),
                                  }}
                                />
                              </Box>
                              <Button
                                component={RouterLink}
                                to={ROUTES.TEST_RUN(tr.id)}
                                size="small"
                                variant="outlined"
                                color="secondary"
                                sx={{ textTransform: 'none', flexShrink: 0 }}
                              >
                                {t('tests.lessonBlock.open', 'Открыть')}
                              </Button>
                            </Box>
                          ))
                        )}
                        {isLessonTeacher ? (
                          <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            component={RouterLink}
                            to={ROUTES.LESSON_TEST_CREATE(lessonId)}
                            startIcon={<AddIcon />}
                            sx={{
                              textTransform: 'none',
                              py: 1.25,
                              borderRadius: 2,
                            }}
                          >
                            {t('tests.actions.createFromLesson', 'Создать тест')}
                          </Button>
                        ) : null}
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {lesson.status === 'completed' && (
                  <Card sx={{ minHeight: 0 }}>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="h6" component="div" fontWeight={700}>
                          {t('homeworks.title', 'Домашние задания')}
                        </Typography>
                        <Chip
                          size="small"
                          label={lessonHomeworks.length}
                          sx={{
                            borderRadius: 999,
                            fontWeight: 700,
                            bgcolor: (theme) => withAlpha(theme.palette.primary.main, 0.14),
                            color: 'primary.main',
                          }}
                        />
                      </Box>

                      {lessonHomeworksError && (
                        <Alert severity="error" onClose={() => setLessonHomeworksError(null)}>
                          {lessonHomeworksError}
                        </Alert>
                      )}

                      {lessonHomeworksLoading ? (
                        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                      ) : lessonHomeworks.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography color="text.secondary">
                            {t('homeworks.empty', 'Нет заданий')}
                          </Typography>
                          {isLessonTeacher && (
                            <Button
                              size="small"
                              variant="contained"
                              color="secondary"
                              startIcon={<AddIcon />}
                              sx={{ alignSelf: 'flex-start' }}
                              onClick={() => {
                                openPostFinishWizard()
                              }}
                            >
                              {t('homeworks.addHomework', 'Добавить домашнее задание')}
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {lessonHomeworks.map((hw) => {
                            const studentName =
                              hw.student?.display_name ??
                              [hw.student?.surname, hw.student?.first_name].filter(Boolean).join(' ') ??
                              hw.student?.email ??
                              `user #${hw.student_id}`

                            const isDone = Boolean(hw.completed_at)
                            const isChecked = Boolean(hw.checked_at)
                            const isAssigned = !isDone && !isChecked

                            const statusLabel = isChecked
                              ? t('homeworks.statusChecked', 'Проверено')
                              : isDone
                                ? t('homeworks.statusCompleted', 'Выполнено (ожидает проверки)')
                                : t('homeworks.statusAssigned', 'Назначено')

                            return (
                              <Box
                                key={hw.id}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 2,
                                  p: 1.5,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.75,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                  <Typography variant="body2" fontWeight={700}>
                                    {isLessonTeacher ? studentName : t('homeworks.yourHomework', 'Домашнее задание')}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isAssigned && (
                                      <IconButton
                                        size="small"
                                        onClick={() => openHomeworkViewModal(hw)}
                                        aria-label="Просмотр домашнего задания"
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    {!isAssigned && (
                                      <Chip
                                        size="small"
                                        label={statusLabel}
                                        sx={{
                                          borderRadius: 999,
                                          fontWeight: 700,
                                          ...(isChecked
                                            ? { bgcolor: (theme) => withAlpha(theme.palette.success.main, 0.14), color: 'success.main' }
                                            : isDone
                                              ? { bgcolor: (theme) => withAlpha(theme.palette.primary.main, 0.14), color: 'primary.main' }
                                              : { bgcolor: 'action.disabledBackground', color: 'text.primary' }),
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>

                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {hw.task_text ?? ''}
                                </Typography>

                                {hw.student_answer_text && (
                                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {t('homeworks.answer', 'Ответ')}: {hw.student_answer_text}
                                  </Typography>
                                )}
                              </Box>
                            )
                          })}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>

      <LessonRescheduleDialog
        open={rescheduleModalOpen && isLessonTeacher}
        lesson={lesson}
        onClose={() => setRescheduleModalOpen(false)}
        onSuccess={() => {
          setRescheduleModalOpen(false)
          void fetchAll()
        }}
      />

      <LessonPostFinishWizard
        open={postFinishWizardOpen && isLessonTeacher}
        lessonId={lessonId}
        participants={participants}
        needsPayment={lesson?.payment_status === 'pending'}
        homeworkDraft={homeworkDraft}
        homeworkEditorNonce={homeworkEditorNonce}
        submittingHomework={submittingHomework}
        homeworkSaveError={homeworkSaveError}
        onHomeworkDraftChange={setHomeworkDraft}
        onClose={() => setPostFinishWizardOpen(false)}
        onSaveHomework={handleSaveHomework}
        onSkipHomework={() => setPostFinishWizardOpen(false)}
        onWordsSaved={fetchLessonWords}
        onPaymentSaved={fetchAll}
      />

      <Dialog
        open={homeworkViewModalOpen}
        onClose={() => {
          setHomeworkViewModalOpen(false)
          setHomeworkToView(null)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('homeworks.afterFinishTitle', 'Домашнее задание')}</DialogTitle>
        <DialogContent>
          {homeworkToView && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t('homeworks.task', 'Задание')}
              </Typography>
              {Array.isArray(homeworkToView.task_json?.blocks) && homeworkToView.task_json.blocks.length > 0 ? (
                <EditorJsField
                  key={`homework-view-task-${homeworkToView.id}`}
                  value={homeworkToView.task_json}
                  onChange={() => {}}
                  readOnly
                  rows={7}
                  hideLabel
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {homeworkToView.task_text ?? ''}
                </Typography>
              )}

              {homeworkToView.student_answer_json && (
                <>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {t('homeworks.answer', 'Ответ')}
                  </Typography>
                  {Array.isArray(homeworkToView.student_answer_json?.blocks) &&
                  homeworkToView.student_answer_json.blocks.length > 0 ? (
                    <EditorJsField
                      key={`homework-view-answer-${homeworkToView.id}`}
                      value={homeworkToView.student_answer_json}
                      onChange={() => {}}
                      readOnly
                      rows={7}
                      hideLabel
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {homeworkToView.student_answer_text ?? ''}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setHomeworkViewModalOpen(false)
              setHomeworkToView(null)
            }}
          >
            {t('common.close', 'Закрыть')}
          </Button>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  )
}

